/**
 * Undo/Redo — local-only op stack for the editor card pane.
 *
 * Server-side EditLog wiring is not yet on the WASM surface; until then,
 * undo/redo is entirely client-side. The stack records inverse operations
 * so Cmd-Z replays them; Cmd-Shift-Z (or Cmd-Y) replays the corresponding
 * redo.
 *
 * Design constraint (UX memo §7 + mockup): undo/redo MUST NOT fire when
 * focus is in an input/textarea/[contenteditable]. The browser's native
 * field-level undo handles those.
 *
 * The stack is a plain array of `{ doFn, undoFn, label }` records.
 * Bookkeeping rules:
 *   - push(record) appends to undo[], clears redo[].
 *   - undo() pops from undo[], runs undoFn, pushes onto redo[].
 *   - redo() pops from redo[], runs doFn, pushes onto undo[].
 *   - Stack capped at MAX_DEPTH (50) by FIFO eviction at the bottom.
 *
 * Toast feedback ("Undid: deleted Figure") fires on each undo/redo via
 * the supplied `showToast(text)` callback. The toast styling lives in
 * `editor.css` (.editor-undo-toast).
 */
const MAX_DEPTH = 50;
/**
 * Create a local undo/redo controller with a bounded op stack.
 *
 * Behaviour contract (pinned by DDT):
 *   - `push(record)` appends to the undo stack and clears the redo stack.
 *     Throws `Error` if `record.doFn` or `record.undoFn` is not a function.
 *     `label` defaults to `"edit"` when omitted.
 *   - Stack eviction: when `undoStack.length > cap` after a push, the oldest
 *     entry is removed by `shift()` (FIFO, from the bottom).
 *   - `undo()` pops from undo[], calls `undoFn()`, pushes the record onto redo[].
 *     Returns `true` if an item was undone, `false` when the stack is empty.
 *     On `undoFn` exception: re-pushes to undo[] so `canUndo()` stays consistent,
 *     fires `showToast` with an error message, then re-throws.
 *   - `redo()` pops from redo[], calls `doFn()`, pushes the record back onto undo[].
 *     Returns `true` if an item was redone, `false` when the stack is empty.
 *     On `doFn` exception: re-pushes to redo[], fires `showToast`, then re-throws.
 *   - `canUndo()` → `undoStack.length > 0`.
 *   - `canRedo()` → `redoStack.length > 0`.
 *   - `clear()` sets both stack lengths to 0 atomically.
 *   - `sizes()` returns `{ undo: number, redo: number }` (snapshot, not live).
 *   - `opts.maxDepth` defaults to `MAX_DEPTH` (50); values ≤ 0 or non-finite are
 *     silently replaced by the default.
 *   - `opts.showToast` is called with a human-readable string after each
 *     successful undo ("Undid: <label>") or redo ("Redid: <label>") and on errors.
 *
 * @param opts - Optional configuration (toast callback + cap override).
 * @returns A `UndoRedoController` instance backed by two private stacks.
 */
export function createUndoRedo(opts = {}) {
    const showToast = typeof opts.showToast === "function" ? opts.showToast : null;
    const cap = Number.isFinite(opts.maxDepth) && opts.maxDepth > 0 ? opts.maxDepth : MAX_DEPTH;
    const undoStack = [];
    const redoStack = [];
    function push(record) {
        if (!record || typeof record.doFn !== "function" || typeof record.undoFn !== "function") {
            throw new Error("undo-redo.push: record must have doFn + undoFn functions");
        }
        const r = {
            doFn: record.doFn,
            undoFn: record.undoFn,
            label: record.label ? String(record.label) : "edit",
        };
        undoStack.push(r);
        // Eviction: we keep the most-recent `cap` entries.
        while (undoStack.length > cap)
            undoStack.shift();
        // Any new edit invalidates the redo branch.
        redoStack.length = 0;
    }
    function undo() {
        const r = undoStack.pop();
        if (!r)
            return false;
        try {
            r.undoFn();
        }
        catch (err) {
            // Surface the error: a broken inverse means the local stack and
            // the IR have diverged. Better a visible error than a silent
            // half-revert. Re-push so canUndo() stays consistent for caller.
            undoStack.push(r);
            if (showToast)
                showToast(`Undo failed: ${err && err.message ? err.message : String(err)}`);
            throw err;
        }
        redoStack.push(r);
        if (showToast)
            showToast(`Undid: ${r.label}`);
        return true;
    }
    function redo() {
        const r = redoStack.pop();
        if (!r)
            return false;
        try {
            r.doFn();
        }
        catch (err) {
            redoStack.push(r);
            if (showToast)
                showToast(`Redo failed: ${err && err.message ? err.message : String(err)}`);
            throw err;
        }
        undoStack.push(r);
        if (showToast)
            showToast(`Redid: ${r.label}`);
        return true;
    }
    function canUndo() { return undoStack.length > 0; }
    function canRedo() { return redoStack.length > 0; }
    function clear() {
        undoStack.length = 0;
        redoStack.length = 0;
    }
    function sizes() {
        return { undo: undoStack.length, redo: redoStack.length };
    }
    return { push, undo, redo, canUndo, canRedo, clear, sizes };
}
/**
 * Mount (or reuse) an `.editor-undo-toast` element and return a `showToast`
 * callback suitable for passing to `createUndoRedo`.
 *
 * Behaviour contract:
 *   - Requires `opts.document`; throws `Error` when absent.
 *   - First looks for an existing `.editor-undo-toast` element in the document;
 *     creates one (div, role="status", aria-live="polite") appended to `body` if absent.
 *   - The returned callback sets `textContent`, adds `.is-visible`, and schedules
 *     removal of `.is-visible` after `opts.ms` milliseconds (default 1800).
 *   - Consecutive calls restart the timer (no stacked timeouts).
 *   - This function is browser-only; it is NOT node-testable without a DOM stub.
 *
 * @param opts - Must include `opts.document`; optional `opts.ms` display duration.
 * @returns A `showToast(text)` callback.
 */
export function mountToast(opts) {
    const doc = opts.document;
    if (!doc)
        throw new Error("mountToast: opts.document required");
    const ms = Number.isFinite(opts.ms) && opts.ms > 0 ? opts.ms : 1800;
    // WHY disable: false positive — querySelector has a deprecated overload for
    // HTMLElementDeprecatedTagNameMap tag names (e.g. <center>, <font>), but
    // ".editor-undo-toast" is a CSS class selector — resolved to the non-deprecated
    // generic overload. The rule fires on overload presence, not on the selected overload.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    let el = doc.querySelector ? doc.querySelector(".editor-undo-toast") : null;
    if (!el) {
        el = doc.createElement("div");
        el.className = "editor-undo-toast";
        el.setAttribute("role", "status");
        el.setAttribute("aria-live", "polite");
        if (doc.body && typeof doc.body.appendChild === "function") {
            doc.body.appendChild(el);
        }
    }
    let timer = null;
    return function showToast(text) {
        (el).textContent = String(text ?? "");
        (el).classList.add("is-visible");
        if (timer)
            clearTimeout(timer);
        timer = setTimeout(() => {
            (el).classList.remove("is-visible");
            timer = null;
        }, ms);
    };
}
